import { useContext, useState } from "react";
import {
  Box,
  Collapse,
  Icon,
  IconButton,
  List,
  ListItemButton,
  Typography,
} from "@mui/material";
import { NavLink } from "react-router-dom";
import { AuthContext } from "src/utils/AuthContext.js";
import { trackNavItemClicked } from "src/utils/sectionAnalytics.js";
import { useIsUsRegion } from "src/utils/useIsUsRegion.js";
import { NAV_CATEGORIES, isExpandable, routeForNode } from "./navTree.js";
import { ELECTIONS_ITEM } from "./navItems.js";
import { isBeforeElectionDay } from "src/components/elections/electionDates.js";
import { resolveNodeIcon } from "./navIcons.js";

/** Left indent per depth so the hierarchy reads at a glance. */
const INDENT = { 0: 1, 1: 2.5, 2: 4, 3: 5.5 };

/** Icon box edge, in px — big enough that filled brand logos stay legible. */
const GLYPH_SIZE = 26;

/** A Material Symbol glyph at the row's size. */
const SymbolGlyph = ({ symbol }) => (
  <Icon baseClassName="material-symbols-outlined" sx={{ fontSize: "1.35rem", color: "icon.primary" }}>
    {symbol}
  </Icon>
);

/**
 * A node's leading icon: the hosted brand logo where we have one (filling the
 * box, no chrome), otherwise a Material Symbol. A logo that fails to load falls
 * back to its symbol so no row is ever blank.
 *
 * @param {{ node: object, isCategory: boolean }} props
 */
const NodeGlyph = ({ node, isCategory }) => {
  const icon = resolveNodeIcon(node, isCategory);
  const [logoFailed, setLogoFailed] = useState(false);

  if (icon.kind === "logo" && !logoFailed) {
    return (
      <Box
        component="img"
        src={icon.url}
        alt=""
        loading="lazy"
        onError={() => setLogoFailed(true)}
        sx={{ width: GLYPH_SIZE, height: GLYPH_SIZE, objectFit: "contain", display: "block" }}
      />
    );
  }
  return <SymbolGlyph symbol={icon.kind === "logo" ? icon.fallback : icon.symbol} />;
};

/** Filled "New" pill for the elections entry. */
const NewBadge = () => (
  <Box
    component="span"
    data-testid="browse-elections-badge"
    sx={{
      ml: 1,
      flex: "none",
      px: 0.75,
      py: 0.2,
      borderRadius: "999px",
      backgroundColor: "text.primary",
      color: "background.paper",
      fontSize: "0.65rem",
      fontWeight: 700,
      lineHeight: 1.5,
      letterSpacing: "0.02em",
      textTransform: "uppercase",
      whiteSpace: "nowrap",
    }}
  >
    New
  </Box>
);

/**
 * US-only Elections entry pinned to the top of the browse tree (above the
 * categories), so it appears identically in the sidebar and the mobile sheet.
 *
 * @param {{ onNavigate?: (node: object) => void }} props
 */
const ElectionsRow = ({ onNavigate }) => (
  <ListItemButton
    component={NavLink}
    to={ELECTIONS_ITEM.to}
    data-testid={ELECTIONS_ITEM.testId}
    onClick={() => onNavigate?.({ tag: "us-elections" })}
    sx={{
      borderRadius: "8px",
      pl: INDENT[0],
      pr: 2,
      py: 0.85,
      color: "text.primary",
      "&.active": { backgroundColor: "overlay.black06", fontWeight: 600 },
    }}
  >
    <Box sx={{ minWidth: 40, height: GLYPH_SIZE, flex: "none", display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
      <SymbolGlyph symbol={ELECTIONS_ITEM.icon} />
    </Box>
    <Typography variant="h5" sx={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      {ELECTIONS_ITEM.label}
    </Typography>
    <NewBadge />
  </ListItemButton>
);

/** Non-clickable "Coming soon" pill for teaser nodes. */
const ComingSoonBadge = () => (
  <Box
    component="span"
    data-testid="coming-soon-badge"
    sx={{
      ml: 1,
      flex: "none",
      px: 0.75,
      py: 0.2,
      borderRadius: "999px",
      backgroundColor: "paper.secondary",
      color: "text.secondary",
      fontSize: "0.65rem",
      fontWeight: 600,
      lineHeight: 1.5,
      whiteSpace: "nowrap",
    }}
  >
    Coming soon
  </Box>
);

/**
 * One nav row. Nodes that route (categories, tags) navigate on row click; the
 * caret toggles expansion without navigating. Pure containers (`group`) toggle
 * on row click. `comingSoon` nodes are non-interactive teasers with a badge.
 *
 * @param {{ node: object, isCategory: boolean, depth: number, expanded: boolean,
 *   onToggle: () => void, onNavigate?: () => void }} props
 */
const NodeRow = ({ node, isCategory, category, depth, expanded, onToggle, onNavigate }) => {
  const route = routeForNode(node, isCategory, category);
  const expandable = isExpandable(node);
  const comingSoon = Boolean(node.comingSoon);

  const handleRowClick = () => {
    if (comingSoon) return;
    if (route) onNavigate?.(node);
    else if (expandable) onToggle();
  };

  const handleCaret = (event) => {
    // Keep the caret from triggering navigation on a routable row.
    event.preventDefault();
    event.stopPropagation();
    onToggle();
  };

  return (
    <ListItemButton
      {...(route ? { component: NavLink, to: route } : {})}
      onClick={handleRowClick}
      disableRipple={comingSoon}
      data-testid={`browse-node-${node.tag}`}
      sx={{
        borderRadius: "8px",
        pl: INDENT[depth] ?? 4,
        // Right padding matches the section headers so carets line up with their chevrons.
        pr: 2,
        py: isCategory ? 0.85 : 0.6,
        color: "text.primary",
        "&.active": { backgroundColor: "overlay.black06", fontWeight: 600 },
        ...(comingSoon && { cursor: "default", "&:hover": { backgroundColor: "transparent" } }),
      }}
    >
      <Box
        sx={{
          // Match NavMenuItem's ListItemIcon (minWidth 40) so the icon→label
          // gap lines up with the You/Premium rows.
          minWidth: 40,
          height: GLYPH_SIZE,
          flex: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        }}
      >
        <NodeGlyph node={node} isCategory={isCategory} />
      </Box>
      <Typography
        variant={isCategory ? "h5" : "body2"}
        sx={{
          flex: 1,
          // Categories match the other top-level sidebar items (h5 default);
          // only nested rows get the lighter weight.
          ...(isCategory ? {} : { fontWeight: 500 }),
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {node.label}
      </Typography>
      {comingSoon && <ComingSoonBadge />}
      {expandable && (
        <IconButton
          size="small"
          aria-label={expanded ? `Collapse ${node.label}` : `Expand ${node.label}`}
          aria-expanded={expanded}
          onClick={handleCaret}
          data-testid={`browse-caret-${node.tag}`}
          // Match the glyph box so an expandable row is the same height as a leaf.
          sx={{ ml: "auto", p: 0, width: GLYPH_SIZE, height: GLYPH_SIZE }}
        >
          <Icon baseClassName="material-symbols-outlined" sx={{ fontSize: "1.25rem", color: "text.quaternary" }}>
            {expanded ? "expand_less" : "expand_more"}
          </Icon>
        </IconButton>
      )}
    </ListItemButton>
  );
};

/**
 * Recursively renders a node and its (collapsible) descendants.
 *
 * @param {{ node: object, isCategory: boolean, depth: number,
 *   openSet: Set<string>, toggle: (slug: string) => void, onNavigate?: () => void }} props
 */
const NodeBranch = ({ node, isCategory, category, depth, openSet, toggle, onNavigate }) => {
  const expandable = isExpandable(node);
  const expanded = openSet.has(node.tag);
  // A top-level node IS its own category; nested nodes inherit it, so genre
  // children know whether they live under Movies (film genre page) or elsewhere.
  const nodeCategory = isCategory ? node : category;
  return (
    <>
      <NodeRow
        node={node}
        isCategory={isCategory}
        category={nodeCategory}
        depth={depth}
        expanded={expanded}
        onToggle={() => toggle(node.tag)}
        onNavigate={onNavigate}
      />
      {expandable && (
        <Collapse in={expanded} unmountOnExit>
          <List disablePadding>
            {node.children.map((child) => (
              <NodeBranch
                key={child.tag}
                node={child}
                isCategory={false}
                category={nodeCategory}
                depth={depth + 1}
                openSet={openSet}
                toggle={toggle}
                onNavigate={onNavigate}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

/**
 * The browse navigation tree — categories with nested tag groups, each row
 * expanding in place. Shared by the mobile browse sheet and the desktop
 * sidebar. Node slugs are unique across the tree, so a flat open-set tracks
 * expansion.
 *
 * @param {{ onNavigate?: () => void, defaultOpen?: string[], testId?: string,
 *   surface?: string }} props - `onNavigate` fires when a routable row is
 *   chosen (used to close the sheet); `surface` labels the analytics event.
 */
const BrowseTree = ({ onNavigate, defaultOpen = [], testId = "browse-tree", surface = "sidebar" }) => {
  const { user } = useContext(AuthContext) ?? {};
  const isUs = useIsUsRegion();
  const [openSet, setOpenSet] = useState(() => new Set(defaultOpen));

  const handleNavigate = (node) => {
    trackNavItemClicked({ user, surface, item: node.tag });
    onNavigate?.(node);
  };

  const toggle = (slug) =>
    setOpenSet((previous) => {
      const next = new Set(previous);
      next.has(slug) ? next.delete(slug) : next.add(slug);
      return next;
    });

  return (
    <List component="nav" aria-label="Browse categories" data-testid={testId} disablePadding>
      {isUs && isBeforeElectionDay() && <ElectionsRow onNavigate={handleNavigate} />}
      {NAV_CATEGORIES.map((category) => (
        <NodeBranch
          key={category.tag}
          node={category}
          isCategory
          depth={0}
          openSet={openSet}
          toggle={toggle}
          onNavigate={handleNavigate}
        />
      ))}
    </List>
  );
};

export default BrowseTree;
